/**
 * AI travel-query parser wrapper.
 *
 * Thin module extracted so useSearchState (and, later, SmartSearchCard /
 * SearchResults) can share one call site. Currently mirrors the body of
 * `processSearchQueryViaBackend` in src/pages/search-plus.tsx — same endpoint,
 * same payload, same response shape. When search-plus.tsx is dismantled in
 * VOY-475/478 the duplicated wrapper there will be removed in favor of this
 * one.
 *
 * Backend: POST /api/search → returns structured params. Agents run on Claude
 * with OpenAI as fallback (see VOY-471 ticket notes).
 */

import { apiFetch } from '@/lib/api'

export interface ParsedTravelQuery {
  origin?: string
  originName?: string
  destination?: string
  destinationName?: string
  departureDate?: string
  returnDate?: string
  travelers?: {
    adults?: number
    children?: number
    childrenAges?: number[]
  }
  /** Alternative passenger key occasionally returned for flight-only queries. */
  passengers?: {
    adults?: number
    children?: number
    childrenAges?: number[]
  }
  cabin?: string
  tripType?: string
  /** Multi-city legs, when detected. */
  segments?: Array<{
    origin?: string
    destination?: string
    date?: string
    originName?: string
    destinationName?: string
  }>
  duration?: number
  preferences?: string[]
}

export interface ParseTravelQueryOptions {
  userId?: string
  /**
   * VOY-474: forwarded to the backend so it knows whether to invoke
   * web-research augmentation. The backend currently logs this and
   * otherwise ignores it; a future ticket can route on it.
   */
  inputMode?: 'chat' | 'web'
}

export async function parseTravelQuery(
  query: string,
  options: ParseTravelQueryOptions = {},
): Promise<ParsedTravelQuery> {
  const res = await apiFetch('/api/search', {
    method: 'POST',
    body: JSON.stringify({
      query,
      userId: options.userId,
      ...(options.inputMode ? { inputMode: options.inputMode } : {}),
    }),
  })
  if (!res.ok) {
    throw new Error('Failed to process search query')
  }
  return res.json()
}
